import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth, gradeAtLeast } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { ArrowLeft, Heart, Users, Edit3, Hand, Trash2, X } from "lucide-react";
import { toast } from "sonner";

export default function PantheonDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [god, setGod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const canClaim = gradeAtLeast(user, "Neophyte");
  const isAdmin = user?.role === "admin";
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/gods/${id}`);
      setGod(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate("/pantheon");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleLike = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/gods/${id}/like`);
      setGod(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const claim = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/gods/${id}/claim`);
      setGod(data);
      toast.success(t("pantheon.claimed_toast", { name: data.name }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const unclaim = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/gods/${id}/unclaim`);
      setGod(data);
      toast.success(t("pantheon.unclaim_toast", { name: data.name }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete(`/gods/${id}`);
      toast.success(t("pantheon.removed_toast"));
      navigate("/pantheon");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(lang === "nl" ? "nl-NL" : "en-US", { day: "numeric", month: "long", year: "numeric" });
    } catch { return iso; }
  };

  if (loading) return <main className="max-w-4xl mx-auto px-6 md:px-10 py-16"><p className="text-[#7A7061] italic">{t("common.loading")}</p></main>;
  if (!god) return null;

  return (
    <main data-testid="pantheon-detail-page" className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <Link to="/pantheon" className="btn-ghost inline-flex items-center gap-2 mb-8" data-testid="pantheon-detail-back-link">
        <ArrowLeft size={14} /> {t("pantheon.back_link")}
      </Link>

      <article className="border border-[#C1B49A] bg-[#EAE0CA]/40">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <div className="md:col-span-5 border-b md:border-b-0 md:border-r border-[#C1B49A]">
            <div
              className="w-full aspect-square bg-[#F4EEDF]"
              style={{
                backgroundImage: god.image_data ? `url(${god.image_data})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-testid="pantheon-detail-image"
            />
          </div>
          <div className="md:col-span-7 p-8 md:p-10">
            <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#7A7061] mb-3">
              {t("pantheon.enshrined_by")} <span className="text-[#4A4238]">{god.creator_name}</span> · {formatDate(god.created_at)}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-[#1A1918] tracking-tight leading-[1.05] mb-6" data-testid="pantheon-detail-name">{god.name}</h1>

            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="grade-pill inline-flex items-center gap-2" data-testid="pantheon-detail-likes" style={{ borderColor: "#8B3A3A", color: "#8B3A3A" }}>
                <Heart size={12} fill={god.user_has_liked ? "#8B3A3A" : "none"} />
                {t("pantheon.likes", { n: god.likes_count })}
              </span>
              <span className="grade-pill inline-flex items-center gap-2" data-testid="pantheon-detail-worshippers">
                <Users size={12} />
                {t("pantheon.worshippers", { n: god.worshippers_count })}
              </span>
            </div>

            <div className="flex flex-wrap gap-3">
              {god.user_is_worshipper ? (
                <>
                  <span className="btn-secondary inline-flex items-center gap-2 cursor-default" data-testid="pantheon-claimed-label">
                    <Hand size={14} /> {t("pantheon.claimed_label")}
                  </span>
                  <Link to={`/pantheon/${god.id}/edit`} className="btn-secondary inline-flex items-center gap-2" data-testid="pantheon-edit-link">
                    <Edit3 size={14} /> {t("common.edit")}
                  </Link>
                  <button
                    data-testid="pantheon-unclaim-btn"
                    type="button"
                    onClick={unclaim}
                    disabled={busy}
                    className="btn-ghost inline-flex items-center gap-2 text-[#8B3A3A] select-none"
                  >
                    <Hand size={14} /> {t("pantheon.unclaim_btn")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    data-testid="pantheon-like-btn"
                    onClick={toggleLike}
                    disabled={busy}
                    className={god.user_has_liked ? "btn-secondary inline-flex items-center gap-2" : "btn-primary inline-flex items-center gap-2"}
                  >
                    <Heart size={14} fill={god.user_has_liked ? "#8B3A3A" : "none"} />
                    {god.user_has_liked ? t("pantheon.unlike_btn") : t("pantheon.like_btn")}
                  </button>
                  {canClaim ? (
                    <button
                      data-testid="pantheon-claim-btn"
                      onClick={claim}
                      disabled={busy}
                      className="btn-secondary inline-flex items-center gap-2"
                    >
                      <Hand size={14} /> {t("pantheon.claim_btn")}
                    </button>
                  ) : (
                    <span className="text-xs italic text-[#7A7061] self-center" data-testid="pantheon-cant-claim">{t("pantheon.cant_claim")}</span>
                  )}
                </>
              )}
              {isAdmin && (
                confirmDelete ? (
                  <div className="flex items-center gap-1">
                    <button
                      data-testid="pantheon-confirm-delete-btn"
                      type="button"
                      onClick={remove}
                      disabled={busy}
                      className="btn-primary inline-flex items-center gap-1.5 select-none whitespace-nowrap"
                    >
                      <Trash2 size={14} /> {t("admin.confirm_remove")}
                    </button>
                    <button
                      data-testid="pantheon-cancel-delete-btn"
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      disabled={busy}
                      className="btn-ghost select-none"
                      style={{ padding: "8px 8px" }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid="pantheon-admin-delete-btn"
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    disabled={busy}
                    className="btn-ghost inline-flex items-center gap-2 text-[#8B3A3A] select-none whitespace-nowrap"
                  >
                    <Trash2 size={14} /> {t("pantheon.delete_btn")}
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-[#C1B49A] p-8 md:p-10">
          <p className="font-body text-[17px] leading-[1.9] text-[#1A1918] whitespace-pre-wrap" data-testid="pantheon-detail-text">{god.text}</p>
        </div>

        <div className="border-t border-[#C1B49A] p-6 md:p-8 bg-[#E0D2B4]/40">
          <p className="label-small mb-3">{t("pantheon.worshippers_heading")} · {god.worshippers_count}</p>
          <ul className="flex flex-wrap gap-2" data-testid="pantheon-worshippers-list">
            {god.worshippers.map((w) => (
              <li key={w.id} className="grade-pill" data-testid={`pantheon-worshipper-${w.id}`}>
                {w.name} · <span className="text-[#8B3A3A]">{w.grade}</span>
              </li>
            ))}
          </ul>
        </div>
      </article>
    </main>
  );
}
